import React from 'react';
import { SystemAlertVisibility } from '../core-types';
import { Anchor, Box, H3, Text } from '@urbaninfrastructure/react-ui-kit';
import {
  ClockAlt,
  RemoveAlt as CloseIcon
} from '@urbaninfrastructure/react-icons';
import { Link, Time } from '../components';
import { isBefore, parseISO } from 'date-fns';
import styled from 'styled-components';

type StateEnum =
  | 'resolved'
  | 'active_now'
  | 'active_to'
  | 'scheduled_from'
  | 'scheduled_from_to'
  | 'ended_at';

const styles = `
@keyframes slideUp {
  from {
    transform: translate(0%, -100%);
  }
  to {
    transform: translate(0%, 0%);
  }
}

@keyframes slideDown {
  from {
    transform: translate(0%, 0%);
  }
  to {
    transform: translate(0%, -100%);
  }
}

.box {
  transition: transform 0.5s ease-in-out;
}

.box.visible {
  animation: slideUp 0.5s forwards;
}

.box.closing {
  animation: slideDown 0.5s ease-in-out;
}
`;

const SystemAlertWrapper = styled(Box)`
  position: relative;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryContrast};
  padding: 1.5rem 2rem;
`;

const StyledBox = styled(Box)`
  &:last-child {
    margin-bottom: 0;
  }
`;

const getSystemAlertState = (now: Date, systemAlert): StateEnum => {
  const { incidentEndTime, incidentStartTime, resolvedAt } = systemAlert;

  if (resolvedAt !== null) {
    return 'resolved';
  }

  if (incidentStartTime && isBefore(now, parseISO(incidentStartTime))) {
    return incidentEndTime ? 'scheduled_from_to' : 'scheduled_from';
  }

  if (incidentEndTime) {
    return isBefore(now, parseISO(incidentEndTime)) ? 'active_to' : 'ended_at';
  }

  return 'active_now';
};

export function setLocalStorageItems(key, value) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, JSON.stringify(value));
  }
}

function getLocalStorageItems(key: string) {
  if (typeof window !== 'undefined') {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      return JSON.parse(storedValue);
    } else {
      return null;
    }
  }
}

const CLOSED_SYSTEM_ALERTS = 'closedSystemAlerts';

const SystemAlert = ({ data, error }) => {
  const [closed, setClosed] = React.useState(false);
  let closedSystemAlerts = getLocalStorageItems(CLOSED_SYSTEM_ALERTS);

  const closedSystemAlertsMap = new Map<string, Date>();
  if (closedSystemAlerts) {
    for (const item of closedSystemAlerts) {
      closedSystemAlertsMap.set(item.id, item.closedAt);
    }
  }

  if (!data || !data.systemAlerts || error) {
    return null;
  }

  let publicSystemAlerts = data.systemAlerts.filter(
    alert =>
      (getSystemAlertState(new Date(), alert) === 'active_now' ||
        getSystemAlertState(new Date(), alert) === 'active_to') &&
      (alert.visibility === SystemAlertVisibility.public_all ||
        alert.visibility === SystemAlertVisibility.public_web) &&
      (!closedSystemAlertsMap.has(alert.id) ||
        alert.updates?.some(update => {
          const closedAt = closedSystemAlertsMap.get(alert.id) ?? new Date();
          return new Date(update.createdAt) > new Date(closedAt);
        }))
  );

  const publicSystemAlertsIds = publicSystemAlerts.map(alert => alert.id);

  const publicSystemAlertsFiltered = publicSystemAlerts;

  const handleClose = () => {
    const existingItems = getLocalStorageItems(CLOSED_SYSTEM_ALERTS) || [];
    const publicIdsSet = new Set(publicSystemAlertsIds);
    const closedAt = new Date();

    // Keep existing items that aren't in current public alerts
    const filteredExistingItems = existingItems.filter(
      item => !publicIdsSet.has(item.id)
    );

    // Create new items for current public alerts
    const newItems = publicSystemAlertsIds.map(id => ({ id, closedAt }));

    setLocalStorageItems(CLOSED_SYSTEM_ALERTS, [
      ...filteredExistingItems,
      ...newItems
    ]);
    setClosed(true);
  };

  const showCloseButton = publicSystemAlertsFiltered.some(
    alert => alert.isDismissible
  );

  return (
    <>
      <style>{styles}</style>
      {publicSystemAlertsFiltered.length > 0 && (
        <SystemAlertWrapper className={`box ${closed ? 'closing' : 'visible'}`}>
          {showCloseButton && (
            <Box
              data-testid="System-Alert-Close"
              className="system-alertclose-button"
              mb="xs"
              position={'absolute'}
              top={'1.25rem'}
              right={'1.25rem'}
              onClick={() => {
                handleClose();
              }}
            >
              <CloseIcon size="1rem" color="primaryContrast" />
            </Box>
          )}
          <Box className="system-alert system-alert-warning" role="alert">
            {publicSystemAlertsFiltered.map(alert => (
              <StyledBox key={alert.id} mb={3}>
                <Link route="system-status" passHref>
                  <Anchor color="primaryContrast">
                    <H3 lineHeight={1} fontWeight={500} mb={'5px'}>
                      {alert.title}
                    </H3>
                  </Anchor>
                </Link>
                <Text fontSize={1} mb={0}>
                  {alert.description}
                </Text>
                {alert.updates &&
                  alert.updates.length > 0 &&
                  alert.updates.map(update => (
                    <Box
                      mt={2}
                      display="flex"
                      alignItems="center"
                      key={update.title}
                    >
                      <Box display="flex" alignItems="center">
                        <ClockAlt size={22} />
                        <Box style={{ marginLeft: 10, fontSize: 16 }}>
                          <Time value={new Date(update.createdAt)} />
                          <Text fontSize="14px">{update.title}</Text>
                        </Box>
                      </Box>
                    </Box>
                  ))}
              </StyledBox>
            ))}
          </Box>
        </SystemAlertWrapper>
      )}
    </>
  );
};

export default SystemAlert;
